import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTeams } from '../contexts/TeamsContext';
import { apiGet, apiPost, apiDelete, apiPatch, getUploadUrl } from '../services/api';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import { toast } from 'sonner';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { format } from 'date-fns';
import {
  Building2, Calendar, Search, ZoomIn, ZoomOut, LocateFixed,
  Map, Bookmark, Settings, LogOut, ChevronDown, X, Check, User, Users, MapPin, Info, Clock,
  Wifi, Menu
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar as CalendarUI } from '../components/ui/calendar';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../components/ui/sheet';

function getDeskState(desk, currentUserId, featureSettings = {}) {
  // If the desk has a booking, the booker "owns" it for that day regardless of desk type
  if (desk.booking) {
    if (desk.booking.user_id === currentUserId) return 'your_booking';
    return 'booked';
  }

  if (desk.type === 'fixed') {
    const allowBookWhenAway = featureSettings.allow_fixed_desk_booking_when_away === 'true';

    // Priority 1: Use Graph workLocationType from presence (authoritative)
    if (desk.presence?.work_location_type && desk.presence.work_location_type !== 'unspecified') {
      const wlt = desk.presence.work_location_type;
      if (wlt === 'timeOff' || desk.presence.out_of_office) {
        return allowBookWhenAway ? 'fixed_away_bookable' : 'fixed_ooo';
      }
      if (wlt === 'office') return 'fixed_in_office';
      if (wlt === 'remote') {
        return allowBookWhenAway ? 'fixed_away_bookable' : 'fixed_remote';
      }
    }
    // Priority 2: Use presence availability/activity as fallback
    if (desk.presence) {
      if (desk.presence.out_of_office || desk.presence.activity === 'OutOfOffice' || desk.presence.activity === 'OffWork') {
        return allowBookWhenAway ? 'fixed_away_bookable' : 'fixed_ooo';
      }
      if (desk.presence.availability === 'Offline' || desk.presence.availability === 'PresenceUnknown') {
        return 'fixed_unknown';
      }
    }
    // Priority 3: Fall back to work_location from work_locations table
    if (desk.work_location === 'office') return 'fixed_in_office';
    if (desk.work_location === 'remote' || desk.work_location === 'elsewhere') {
      return allowBookWhenAway ? 'fixed_away_bookable' : 'fixed_remote';
    }
    if (desk.work_location === 'out_of_office') {
      return allowBookWhenAway ? 'fixed_away_bookable' : 'fixed_ooo';
    }
    return 'fixed_unknown';
  }
  return 'available';
}

const stateColors = {
  available:            { bg: 'bg-[hsl(142,76%,36%)]', border: 'border-[hsl(142,60%,28%)]', label: 'Available', hex: '#22a34a' },
  booked:              { bg: 'bg-[hsl(0,72%,50%)]',   border: 'border-[hsl(0,65%,40%)]',   label: 'Booked',    hex: '#d93535' },
  fixed_in_office:     { bg: 'bg-[hsl(217,90%,50%)]', border: 'border-[hsl(217,80%,40%)]', label: 'In Office', hex: '#2563eb' },
  fixed_remote:        { bg: 'bg-[hsl(270,60%,55%)]', border: 'border-[hsl(270,50%,45%)] border-dashed', label: 'Remote', hex: '#8b5cf6' },
  fixed_ooo:           { bg: 'bg-[hsl(30,90%,50%)]',  border: 'border-[hsl(30,80%,40%)]',  label: 'Out of Office', hex: '#ea7c0e' },
  fixed_away_bookable: { bg: 'bg-[hsl(142,76%,36%)]', border: 'border-[hsl(142,60%,28%)] border-dashed', label: 'Away - Bookable', hex: '#22a34a' },
  fixed_unknown:       { bg: 'bg-[hsl(210,15%,65%)] opacity-60', border: 'border-[hsl(210,10%,55%)]', label: 'Unknown', hex: '#94a3b8' },
  your_booking:        { bg: 'bg-[hsl(186,72%,36%)]', border: 'border-[hsl(186,65%,28%)]', label: 'Your Booking', hex: '#0d9488' },
};

function DeskMarker({ desk, state, isHighlighted, onClick }) {
  const style = stateColors[state] || stateColors.available;
  const isYours = state === 'your_booking';

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            data-testid={`desk-marker-${desk.label}`}
            className={`absolute transform -translate-x-1/2 -translate-y-1/2 z-10
              rounded-full border-2 shadow-sm cursor-pointer
              h-7 w-7 flex items-center justify-center
              transition-shadow duration-150 hover:shadow-md
              focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2
              ${style.bg} ${style.border}
              ${isYours ? 'ring-2 ring-[hsl(186,78%,44%)]' : ''}
              ${isHighlighted ? 'ring-4 ring-yellow-400 z-20 scale-125' : ''}
            `}
            style={{ left: `${desk.x_percent}%`, top: `${desk.y_percent}%` }}
            onClick={(e) => { e.stopPropagation(); onClick(desk); }}
            aria-label={`Desk ${desk.label}, ${style.label}`}
          >
            {isYours && (
              <span className="absolute inset-[-6px] rounded-full border border-[hsl(186,78%,44%)] animate-desk-pulse" />
            )}
          </button>
        </TooltipTrigger>
        <TooltipContent side="top" className="text-xs">
          <p className="font-semibold">{desk.label}</p>
          <p className="text-primary-foreground/70">{style.label}</p>
          {desk.booking ? (
            <p>{desk.booking.booker_name}</p>
          ) : desk.type === 'fixed' && desk.assigned_user_name ? (
            <p>{desk.assigned_user_name}</p>
          ) : null}
          {desk.presence && (
            <p className="text-primary-foreground/70">
              {desk.presence.availability}{desk.presence.out_of_office ? ' (OOO)' : ''}
              {desk.presence.work_location_type && desk.presence.work_location_type !== 'unspecified' && (
                <> &middot; {desk.presence.work_location_type}</>
              )}
            </p>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function MeetingRoomMarker({ room, onClick }) {
  if (!room.x_percent || !room.y_percent) return null;

  const isBusy = room.availability === 'busy';
  const colorClasses = isBusy
    ? 'bg-red-100 border-red-400 text-red-700'
    : 'bg-emerald-100 border-emerald-400 text-emerald-700';

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            data-testid={`room-marker-${room.display_name}`}
            className={`absolute transform -translate-x-1/2 -translate-y-1/2 z-10
              rounded-md border-2 shadow-sm cursor-pointer
              h-7 w-7 flex items-center justify-center
              transition-colors duration-150
              focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring
              ${colorClasses}`}
            style={{ left: `${room.x_percent}%`, top: `${room.y_percent}%` }}
            onClick={(e) => { e.stopPropagation(); onClick(room); }}
          >
            <span className="text-[7px] font-bold leading-none select-none">RM</span>
          </button>
        </TooltipTrigger>
        <TooltipContent side="top" className="text-xs">
          <p className="font-semibold">{room.display_name}</p>
          <p className={isBusy ? 'text-red-300 font-medium' : 'text-emerald-300 font-medium'}>
            {isBusy ? 'Busy' : 'Available'}
          </p>
          {room.current_event && (
            <p className="text-primary-foreground/70">{room.current_event.subject || 'Meeting'}</p>
          )}
          {room.capacity > 0 && <p className="text-primary-foreground/70">Capacity: {room.capacity}</p>}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function RoomDetailSheet({ room, isOpen, onClose, dateStr }) {
  const [schedule, setSchedule] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!room || !isOpen) return;
    setLoading(true);
    apiGet(`/rooms/${room.id}/schedule`, { date: dateStr })
      .then(data => { setSchedule(data); setLoading(false); })
      .catch(() => { setSchedule(null); setLoading(false); });
  }, [room, isOpen, dateStr]);

  if (!room) return null;
  const isBusy = room.availability === 'busy';

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent side="right" className="w-[360px] sm:w-[400px]" data-testid="room-detail-sheet">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            {room.display_name}
          </SheetTitle>
          <SheetDescription>Meeting Room</SheetDescription>
        </SheetHeader>

        <div className="mt-4 space-y-4">
          {/* Current Status */}
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Status</span>
                <Badge
                  data-testid="room-status-badge"
                  variant={isBusy ? 'destructive' : 'default'}
                  className={isBusy ? '' : 'bg-emerald-600 hover:bg-emerald-700'}
                >
                  {isBusy ? 'Busy' : 'Available'}
                </Badge>
              </div>
              {room.current_event && (
                <div className="mt-2 text-xs text-muted-foreground">
                  <p className="font-medium text-foreground">{room.current_event.subject || 'Meeting in progress'}</p>
                  {room.current_event.organizer && <p>Organizer: {room.current_event.organizer}</p>}
                  <p>
                    {formatTime(room.current_event.start_time)} - {formatTime(room.current_event.end_time)}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Room Info */}
          <Card>
            <CardContent className="pt-4 space-y-2">
              {room.capacity > 0 && (
                <div className="flex items-center gap-2 text-sm">
                  <Users className="h-3.5 w-3.5 text-muted-foreground" />
                  <span>Capacity: {room.capacity}</span>
                </div>
              )}
              {room.building && (
                <div className="flex items-center gap-2 text-sm">
                  <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
                  <span>{room.building}{room.floor_label ? `, ${room.floor_label}` : ''}</span>
                </div>
              )}
              {room.email && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <span className="truncate">{room.email}</span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Today's Schedule */}
          <Card>
            <CardHeader className="pb-2 pt-4">
              <CardTitle className="text-sm flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Today's Schedule
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-4">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary" />
                </div>
              ) : schedule?.events?.length > 0 ? (
                <div className="space-y-2">
                  {schedule.events.map((event, idx) => (
                    <div
                      key={idx}
                      data-testid={`room-event-${idx}`}
                      className="flex items-start gap-3 py-2 border-b last:border-0"
                    >
                      <div className="w-1 h-full min-h-[2rem] rounded bg-red-400 shrink-0 mt-0.5" />
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{event.subject || 'Meeting'}</p>
                        <p className="text-xs text-muted-foreground">
                          {formatTime(event.start_time)} - {formatTime(event.end_time)}
                        </p>
                        {event.organizer && (
                          <p className="text-xs text-muted-foreground">{event.organizer}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4" data-testid="room-no-events">
                  No meetings scheduled today
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function formatTime(isoString) {
  if (!isoString) return '';
  try {
    // API returns UTC times — ensure Z suffix so Date parses as UTC,
    // then toLocaleTimeString converts to the user's local timezone.
    const utcStr = isoString.endsWith('Z') ? isoString : isoString + 'Z';
    const d = new Date(utcStr);
    if (isNaN(d.getTime())) return isoString;
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch {
    return isoString;
  }
}


function DeskDetailSheet({ desk, state, isOpen, onClose, onBook, onCancel, onCheckin, currentUserId, requireCheckin }) {
  if (!desk) return null;
  const style = stateColors[state] || stateColors.available;
  const isFixed = desk.type === 'fixed';
  const isMyBooking = desk.booking?.user_id === currentUserId;
  const canBook = state === 'available' || state === 'fixed_away_bookable';
  const canCancel = isMyBooking && desk.booking?.status !== 'cancelled';
  const canCheckin = isMyBooking && desk.booking?.status === 'confirmed';

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="w-[400px] sm:w-[440px]" data-testid="booking-drawer">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5" />
            Desk {desk.label}
          </SheetTitle>
          <SheetDescription asChild>
            <div>
              <Badge variant="secondary" className="mt-1">
                <span className={`inline-block h-2 w-2 rounded-full mr-1.5 ${style.bg}`} />
                {style.label}
              </Badge>
              {isFixed && <Badge variant="outline" className="ml-2">Permanent Desk</Badge>}
            </div>
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-4">
          {/* Assigned User (Fixed Desk) - shown only when no booking (owner is at their desk) */}
          {isFixed && desk.assigned_user_name && !desk.booking && (
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Avatar className="h-10 w-10">
                    {desk.assigned_user_photo && <AvatarImage src={desk.assigned_user_photo} alt={desk.assigned_user_name} />}
                    <AvatarFallback className="bg-primary/10 text-primary font-semibold">
                      {desk.assigned_user_name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-semibold text-sm">{desk.assigned_user_name}</p>
                    <p className="text-xs text-muted-foreground">{desk.assigned_user_email}</p>
                    {desk.assigned_user_job_title && (
                      <p className="text-xs text-muted-foreground">{desk.assigned_user_job_title}</p>
                    )}
                  </div>
                </div>
                {desk.work_location && (
                  <div className="mt-3 flex items-center gap-2">
                    <Info className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="text-xs text-muted-foreground capitalize">
                      Work location: {desk.presence?.work_location_type && desk.presence.work_location_type !== 'unspecified'
                        ? desk.presence.work_location_type
                        : desk.work_location}
                    </span>
                  </div>
                )}
                {desk.presence && (
                  <div className="mt-1 flex items-center gap-2">
                    <Wifi className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">
                      Presence: {desk.presence.availability}
                      {desk.presence.out_of_office && ' (Out of Office)'}
                    </span>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Booker Info - shown for any desk with an active booking */}
          {desk.booking && (
            <Card>
              <CardContent className="p-4">
                <p className="text-xs font-medium text-muted-foreground mb-2">Booked by</p>
                <div className="flex items-center gap-3">
                  <Avatar className="h-10 w-10">
                    {desk.booking.booker_photo && <AvatarImage src={desk.booking.booker_photo} alt={desk.booking.booker_name} />}
                    <AvatarFallback className="bg-accent/10 text-accent font-semibold">
                      {desk.booking.booker_name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-semibold text-sm">{desk.booking.booker_name}</p>
                    <p className="text-xs text-muted-foreground">{desk.booking.booker_email}</p>
                    {desk.booking.booker_job_title && (
                      <p className="text-xs text-muted-foreground">{desk.booking.booker_job_title}</p>
                    )}
                  </div>
                </div>
                <div className="mt-3 flex items-center gap-2">
                  <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="text-xs text-muted-foreground">
                    Status: <Badge variant="outline" className="ml-1 text-[10px]">{desk.booking.status}</Badge>
                  </span>
                </div>
                {isFixed && desk.assigned_user_name && (
                  <div className="mt-2 flex items-center gap-2">
                    <Info className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">
                      Normally assigned to {desk.assigned_user_name}
                    </span>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Tags */}
          {desk.tags && desk.tags.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {desk.tags.map((tag, i) => (
                <Badge key={i} variant="secondary" className="text-xs">{tag}</Badge>
              ))}
            </div>
          )}

          <Separator />

          {/* Actions */}
          <div className="space-y-2">
            {canBook && (
              <>
                <Button
                  className="w-full"
                  data-testid="booking-confirm-button"
                  onClick={() => onBook(desk)}
                >
                  <Check className="h-4 w-4 mr-2" /> Book This Desk
                </Button>
                {state === 'fixed_away_bookable' && (
                  <p className="text-xs text-center text-muted-foreground">
                    The assigned user is away — this desk is temporarily available.
                  </p>
                )}
                {requireCheckin && (
                  <p className="text-xs text-center text-amber-600">
                    Check-in is required for this booking.
                  </p>
                )}
              </>
            )}
            {canCheckin && (
              <Button
                className="w-full"
                variant="outline"
                onClick={() => onCheckin(desk.booking.id)}
              >
                <MapPin className="h-4 w-4 mr-2" /> Check In
              </Button>
            )}
            {canCancel && (
              <Button
                className="w-full"
                variant="destructive"
                data-testid="booking-cancel-button"
                onClick={() => onCancel(desk.booking.id)}
              >
                <X className="h-4 w-4 mr-2" /> Cancel Booking
              </Button>
            )}
            {isFixed && state !== 'fixed_away_bookable' && !desk.booking && (
              <p className="text-xs text-center text-muted-foreground">
                This is a permanently assigned desk and cannot be booked.
              </p>
            )}
            {state === 'booked' && !isMyBooking && (
              <p className="text-xs text-center text-muted-foreground">
                This desk is already booked for the selected date.
              </p>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default function MapPage() {
  const { user, logout, isAdmin, isInTeams } = useAuth();
  const { isInTeams: teamsDetected } = useTeams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const inTeams = isInTeams || teamsDetected;

  // Track mobile viewport for conditional rendering
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  const [floors, setFloors] = useState([]);
  const [selectedFloorId, setSelectedFloorId] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [desks, setDesks] = useState([]);
  const [floorData, setFloorData] = useState(null);
  const [selectedDesk, setSelectedDesk] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [highlightedDeskId, setHighlightedDeskId] = useState(null);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [showMyBookings, setShowMyBookings] = useState(false);
  const [myBookings, setMyBookings] = useState([]);
  const [showWhosIn, setShowWhosIn] = useState(false);
  const [whosInData, setWhosInData] = useState(null);
  const [whosInFilter, setWhosInFilter] = useState('');
  const [whosInLocation, setWhosInLocation] = useState('office'); // 'office' or 'remote'
  const [rooms, setRooms] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [roomSheetOpen, setRoomSheetOpen] = useState(false);
  const [mobilePanel, setMobilePanel] = useState(null); // 'search' | 'bookings' | 'whosIn' | null
  const [featureSettings, setFeatureSettings] = useState({});
  const transformRef = useRef(null);

  const dateStr = format(selectedDate, 'yyyy-MM-dd');

  // Load floors
  useEffect(() => {
    apiGet('/floors')
      .then(data => {
        setFloors(data);
        if (data.length > 0 && !selectedFloorId) {
          setSelectedFloorId(data[0].id);
        }
        setLoading(false);
      })
      .catch(err => { console.error(err); setLoading(false); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load desks for selected floor and date
  useEffect(() => {
    if (!selectedFloorId) return;
    setLoading(true);
    apiGet(`/floors/${selectedFloorId}/desks`, { date: dateStr })
      .then(data => {
        setDesks(data.desks || []);
        setFloorData(data.floor || null);
        setRooms(data.rooms || []);
        if (data.settings) setFeatureSettings(data.settings);
        setLoading(false);
      })
      .catch(err => { console.error(err); setLoading(false); });
  }, [selectedFloorId, dateStr]);

  // Presence polling — configurable refresh interval
  useEffect(() => {
    if (!selectedFloorId) return;

    // Default 60 seconds — increased from typical 30s to reduce D1 load
    const intervalMs = 60000;
    let cancelled = false;

    const interval = setInterval(() => {
      if (cancelled) return;
      apiGet(`/floors/${selectedFloorId}/desks`, { date: dateStr })
        .then(data => {
          setDesks(data.desks || []);
          setRooms(data.rooms || []);
          if (data.settings) setFeatureSettings(data.settings);
        })
        .catch(() => {});
    }, intervalMs);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [selectedFloorId, dateStr]);

  // Load my bookings (only when date or floor changes, not on every poll)
  useEffect(() => {
    apiGet('/bookings/me')
      .then(data => setMyBookings(data))
      .catch(console.error);
  }, [dateStr, selectedFloorId]);

  // Load who's in the office data
  useEffect(() => {
    if (!showWhosIn) return;
    setWhosInData(null);
    apiGet('/whos-in', { date: dateStr, location: whosInLocation })
      .then(data => setWhosInData(data))
      .catch(() => setWhosInData(null));
  }, [dateStr, showWhosIn, desks, whosInLocation]);

  // Mobile panel helpers
  const togglePanel = useCallback((panel) => {
    if (panel === 'search') {
      const newVal = !showSearch;
      setShowSearch(newVal);
      setShowMyBookings(false);
      setShowWhosIn(false);
      setMobilePanel(newVal ? 'search' : null);
    } else if (panel === 'bookings') {
      const newVal = !showMyBookings;
      setShowMyBookings(newVal);
      setShowSearch(false);
      setShowWhosIn(false);
      setMobilePanel(newVal ? 'bookings' : null);
    } else if (panel === 'whosIn') {
      const newVal = !showWhosIn;
      setShowWhosIn(newVal);
      setShowSearch(false);
      setShowMyBookings(false);
      setMobilePanel(newVal ? 'whosIn' : null);
    } else {
      setShowSearch(false);
      setShowMyBookings(false);
      setShowWhosIn(false);
      setMobilePanel(null);
    }
  }, [showSearch, showMyBookings, showWhosIn]);

  // Search
  useEffect(() => {
    if (searchQuery.length < 2) { setSearchResults([]); return; }
    const timeout = setTimeout(() => {
      apiGet('/users/search', { q: searchQuery, date: dateStr })
        .then(data => setSearchResults(data))
        .catch(console.error);
    }, 300);
    return () => clearTimeout(timeout);
  }, [searchQuery, dateStr]);

  const handleBookDesk = useCallback(async (desk) => {
    try {
      await apiPost('/bookings', { desk_id: desk.id, date: dateStr });
      toast.success(`Desk ${desk.label} booked for ${dateStr}`);
      setSheetOpen(false);
      // Refresh
      const data = await apiGet(`/floors/${selectedFloorId}/desks`, { date: dateStr });
      setDesks(data.desks || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to book desk');
    }
  }, [dateStr, selectedFloorId]);

  const handleCancelBooking = useCallback(async (bookingId) => {
    try {
      await apiDelete(`/bookings/${bookingId}`);
      toast.success('Booking cancelled');
      setSheetOpen(false);
      const data = await apiGet(`/floors/${selectedFloorId}/desks`, { date: dateStr });
      setDesks(data.desks || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel');
    }
  }, [dateStr, selectedFloorId]);

  const handleCheckin = useCallback(async (bookingId) => {
    try {
      await apiPatch(`/bookings/${bookingId}/checkin`);
      toast.success('Checked in successfully!');
      setSheetOpen(false);
      const data = await apiGet(`/floors/${selectedFloorId}/desks`, { date: dateStr });
      setDesks(data.desks || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Check-in failed');
    }
  }, [dateStr, selectedFloorId]);

  const handleDeskClick = useCallback((desk) => {
    setSelectedDesk(desk);
    setSheetOpen(true);
  }, []);

  const handleRoomClick = useCallback((room) => {
    setSelectedRoom(room);
    setRoomSheetOpen(true);
  }, []);


  const handleSearchSelect = useCallback((person) => {
    // Use desk info from search results (enriched by backend)
    if (person.desk) {
      // Switch floor if needed
      if (person.desk.floor_id !== selectedFloorId) {
        setSelectedFloorId(person.desk.floor_id);
      }
      setHighlightedDeskId(person.desk.id);
      setShowSearch(false);
      setMobilePanel(null);
      setSearchQuery('');
      setTimeout(() => setHighlightedDeskId(null), 3000);
    } else {
      toast.info(`${person.display_name} has no desk assigned for the selected date`);
    }
  }, [selectedFloorId]);

  const selectedDeskState = selectedDesk ? getDeskState(selectedDesk, user?.id, featureSettings) : 'available';

  return (
    <div className="h-screen w-screen flex flex-col md:flex-row overflow-hidden bg-background">
      {/* Desktop left sidebar rail — hidden on mobile */}
      <div className="hidden md:flex w-[72px] border-r bg-card flex-col items-center py-4 gap-2 z-30 shrink-0">
        <div className="h-10 w-10 rounded-xl bg-primary flex items-center justify-center mb-4">
          <Building2 className="h-5 w-5 text-primary-foreground" />
        </div>

        <TooltipProvider delayDuration={100}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="icon" className="h-10 w-10" data-testid="nav-map"
                onClick={() => togglePanel(null)}>
                <Map className="h-5 w-5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="right">Floor Map</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="icon" className="h-10 w-10" data-testid="nav-search"
                onClick={() => togglePanel('search')}>
                <Search className="h-5 w-5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="right">Find Person</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="icon" className="h-10 w-10" data-testid="nav-bookings"
                onClick={() => togglePanel('bookings')}>
                <Bookmark className="h-5 w-5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="right">My Bookings</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="icon" className="h-10 w-10" data-testid="nav-whos-in"
                onClick={() => togglePanel('whosIn')}>
                <Users className="h-5 w-5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="right">Who's In Today</TooltipContent>
          </Tooltip>

          {isAdmin && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" className="h-10 w-10" data-testid="nav-admin"
                  onClick={() => navigate('/admin')}>
                  <Settings className="h-5 w-5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right">Admin Panel</TooltipContent>
            </Tooltip>
          )}
        </TooltipProvider>

        <div className="flex-1" />

        {!inTeams && (
          <TooltipProvider delayDuration={100}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" className="h-10 w-10" onClick={logout} data-testid="nav-logout">
                  <LogOut className="h-5 w-5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right">Sign Out</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}

        <Avatar className="h-9 w-9 mt-1">
          <AvatarFallback className="text-xs bg-primary/10 text-primary font-semibold">
            {user?.display_name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
          </AvatarFallback>
        </Avatar>
      </div>

      {/* Desktop Search panel */}
      {showSearch && (
        <div className="hidden md:flex w-[320px] border-r bg-card z-20 flex-col shrink-0">
          <div className="p-4 border-b">
            <h3 className="font-semibold text-sm mb-3">Find a Person</h3>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                data-testid="person-search-input"
                placeholder="Search by name or email..."
                className="pl-9"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                autoFocus
              />
            </div>
          </div>
          <ScrollArea className="flex-1">
            {searchResults.map(person => (
              <button
                key={person.id}
                data-testid="person-search-result-item"
                className="w-full text-left p-3 hover:bg-muted/50 flex items-center gap-3 border-b transition-colors duration-150"
                onClick={() => handleSearchSelect(person)}
              >
                <Avatar className="h-8 w-8">
                  {person.photo_url && <AvatarImage src={person.photo_url} alt={person.display_name} />}
                  <AvatarFallback className="text-xs bg-primary/10">
                    {person.display_name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">{person.display_name}</p>
                  <p className="text-xs text-muted-foreground truncate">{person.email}</p>
                  {person.job_title && <p className="text-xs text-muted-foreground">{person.job_title}</p>}
                  {person.desk ? (
                    <div className="flex items-center gap-1 mt-0.5">
                      <MapPin className="h-3 w-3 text-primary shrink-0" />
                      <span className="text-xs text-primary font-medium">{person.desk.label}</span>
                      <span className="text-xs text-muted-foreground">- {person.desk.floor_name}</span>
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground mt-0.5">No desk assigned</p>
                  )}
                </div>
              </button>
            ))}
            {searchQuery.length >= 2 && searchResults.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-6">No results found</p>
            )}
          </ScrollArea>
        </div>
      )}

      {/* Desktop My Bookings panel */}
      {showMyBookings && (
        <div className="hidden md:flex w-[340px] border-r bg-card z-20 flex-col shrink-0">
          <div className="p-4 border-b">
            <h3 className="font-semibold text-sm">My Bookings</h3>
          </div>
          <ScrollArea className="flex-1" data-testid="my-bookings-panel">
            {myBookings.filter(b => b.status !== 'cancelled').length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-6">No active bookings</p>
            )}
            {myBookings.filter(b => b.status !== 'cancelled').map(booking => (
              <div key={booking.id} className="p-3 border-b hover:bg-muted/30 transition-colors duration-150">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">{booking.desk_label}</p>
                    <p className="text-xs text-muted-foreground">{booking.floor_name}</p>
                    <p className="text-xs text-muted-foreground">{booking.date}</p>
                  </div>
                  <div className="flex items-center gap-1">
                    <Badge variant={booking.status === 'checked_in' ? 'default' : 'secondary'} className="text-[10px]">
                      {booking.status}
                    </Badge>
                    {booking.status === 'confirmed' && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        data-testid="my-bookings-cancel-button"
                        onClick={() => handleCancelBooking(booking.id)}
                      >
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </ScrollArea>
        </div>
      )}

      {/* Desktop Who's In the Office panel */}
      {showWhosIn && (
        <div className="hidden md:flex w-[340px] border-r bg-card z-20 flex-col shrink-0" data-testid="whos-in-panel">
          <div className="p-4 border-b space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-sm flex items-center gap-2">
                <Users className="h-4 w-4 text-primary" />
                Who's In Today
              </h3>
              {whosInData && (
                <Badge variant="secondary" data-testid="whos-in-count">
                  {whosInData.total} {whosInLocation === 'office' ? 'in office' : 'remote'}
                </Badge>
              )}
            </div>
            {/* Location toggle */}
            <div className="flex gap-1 bg-muted rounded-lg p-0.5" data-testid="whos-in-location-toggle">
              <button
                data-testid="whos-in-toggle-office"
                className={`flex-1 text-xs font-medium py-1.5 px-3 rounded-md transition-colors ${whosInLocation === 'office' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                onClick={() => setWhosInLocation('office')}
              >
                <MapPin className="h-3 w-3 inline mr-1" />
                In Office
              </button>
              <button
                data-testid="whos-in-toggle-remote"
                className={`flex-1 text-xs font-medium py-1.5 px-3 rounded-md transition-colors ${whosInLocation === 'remote' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                onClick={() => setWhosInLocation('remote')}
              >
                <Wifi className="h-3 w-3 inline mr-1" />
                Remote
              </button>
            </div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                data-testid="whos-in-search"
                placeholder="Filter by name..."
                className="pl-9 h-8 text-sm"
                value={whosInFilter}
                onChange={(e) => setWhosInFilter(e.target.value)}
              />
            </div>
            {whosInData && whosInLocation === 'office' && (
              <div className="flex gap-3 text-xs text-muted-foreground">
                <span className="flex items-center gap-1">
                  <MapPin className="h-3 w-3" /> {whosInData.with_desk} at desk
                </span>
                <span className="flex items-center gap-1">
                  <User className="h-3 w-3" /> {whosInData.without_desk} no desk
                </span>
              </div>
            )}
          </div>
          <ScrollArea className="flex-1">
            {!whosInData && (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary" />
              </div>
            )}
            {whosInData && whosInData.users
              .filter(u => !whosInFilter || u.display_name.toLowerCase().includes(whosInFilter.toLowerCase()))
              .map(person => (
              <button
                key={person.id}
                data-testid="whos-in-person-item"
                className="w-full text-left p-3 hover:bg-muted/50 flex items-center gap-3 border-b transition-colors duration-150"
                onClick={() => {
                  if (person.desk) {
                    if (person.desk.floor_id !== selectedFloorId) {
                      setSelectedFloorId(person.desk.floor_id);
                    }
                    setHighlightedDeskId(person.desk.id);
                    setTimeout(() => setHighlightedDeskId(null), 3000);
                  } else {
                    toast.info(`${person.display_name} is in the office but has no assigned desk`);
                  }
                }}
              >
                <Avatar className="h-8 w-8 shrink-0">
                  {person.photo_url && <AvatarImage src={person.photo_url} alt={person.display_name} />}
                  <AvatarFallback className="text-xs bg-emerald-500/10 text-emerald-700">
                    {person.display_name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">{person.display_name}</p>
                  {person.job_title && (
                    <p className="text-xs text-muted-foreground truncate">{person.job_title}</p>
                  )}
                  {person.desk ? (
                    <div className="flex items-center gap-1 mt-0.5">
                      <MapPin className="h-3 w-3 text-primary shrink-0" />
                      <span className="text-xs text-primary font-medium">{person.desk.label}</span>
                      <span className="text-xs text-muted-foreground">- {person.desk.floor_name}</span>
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground mt-0.5">No desk assigned</p>
                  )}
                </div>
              </button>
            ))}
            {whosInData && whosInData.users.filter(u => !whosInFilter || u.display_name.toLowerCase().includes(whosInFilter.toLowerCase())).length === 0 && (
              <div className="text-center py-8">
                <Users className="h-8 w-8 mx-auto mb-2 text-muted-foreground/30" />
                <p className="text-sm text-muted-foreground">
                  {whosInFilter ? 'No matching people found' : whosInLocation === 'office' ? 'No one marked as in-office for this date' : 'No one marked as remote for this date'}
                </p>
              </div>
            )}
          </ScrollArea>
        </div>
      )}

      {/* Mobile slide-up panel */}
      {mobilePanel && isMobile && (
        <Sheet open={!!mobilePanel} onOpenChange={(open) => { if (!open) togglePanel(null); }}>
          <SheetContent side="bottom" className="md:hidden h-[70vh] rounded-t-2xl p-0" data-testid="mobile-panel-sheet">
            <SheetHeader className="sr-only">
              <SheetTitle>
                {mobilePanel === 'search' && 'Find a Person'}
                {mobilePanel === 'bookings' && 'My Bookings'}
                {mobilePanel === 'whosIn' && "Who's In Today"}
              </SheetTitle>
            </SheetHeader>
            <div className="flex flex-col h-full">
              <div className="p-4 border-b">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-sm">
                    {mobilePanel === 'search' && 'Find a Person'}
                    {mobilePanel === 'bookings' && 'My Bookings'}
                    {mobilePanel === 'whosIn' && 'Who\'s In Today'}
                  </h3>
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => togglePanel(null)}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                {mobilePanel === 'search' && (
                  <div className="relative mt-3">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      data-testid="mobile-person-search-input"
                      placeholder="Search by name or email..."
                      className="pl-9"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      autoFocus
                    />
                  </div>
                )}
                {mobilePanel === 'whosIn' && (
                  <>
                    {/* Mobile location toggle */}
                    <div className="flex gap-1 bg-muted rounded-lg p-0.5 mt-3" data-testid="mobile-whos-in-location-toggle">
                      <button
                        data-testid="mobile-whos-in-toggle-office"
                        className={`flex-1 text-xs font-medium py-1.5 px-3 rounded-md transition-colors ${whosInLocation === 'office' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                        onClick={() => setWhosInLocation('office')}
                      >
                        <MapPin className="h-3 w-3 inline mr-1" />
                        In Office
                      </button>
                      <button
                        data-testid="mobile-whos-in-toggle-remote"
                        className={`flex-1 text-xs font-medium py-1.5 px-3 rounded-md transition-colors ${whosInLocation === 'remote' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                        onClick={() => setWhosInLocation('remote')}
                      >
                        <Wifi className="h-3 w-3 inline mr-1" />
                        Remote
                      </button>
                    </div>
                    <div className="relative mt-3">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        data-testid="mobile-whos-in-search"
                        placeholder="Filter by name..."
                        className="pl-9 h-8 text-sm"
                        value={whosInFilter}
                        onChange={(e) => setWhosInFilter(e.target.value)}
                      />
                    </div>
                    {whosInData && (
                      <div className="flex gap-3 text-xs text-muted-foreground mt-2">
                        <Badge variant="secondary" data-testid="mobile-whos-in-count">
                          {whosInData.total} {whosInLocation === 'office' ? 'in office' : 'remote'}
                        </Badge>
                      </div>
                    )}
                  </>
                )}
              </div>
              <ScrollArea className="flex-1">
                {/* Search results */}
                {mobilePanel === 'search' && searchResults.map(person => (
                  <button
                    key={person.id}
                    data-testid="mobile-person-search-result"
                    className="w-full text-left p-3 hover:bg-muted/50 flex items-center gap-3 border-b"
                    onClick={() => { handleSearchSelect(person); togglePanel(null); }}
                  >
                    <Avatar className="h-8 w-8">
                      {person.photo_url && <AvatarImage src={person.photo_url} alt={person.display_name} />}
                      <AvatarFallback className="text-xs bg-primary/10">
                      {person.display_name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                    </AvatarFallback></Avatar>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium truncate">{person.display_name}</p>
                      <p className="text-xs text-muted-foreground truncate">{person.email}</p>
                      {person.desk ? (
                        <div className="flex items-center gap-1 mt-0.5">
                          <MapPin className="h-3 w-3 text-primary shrink-0" />
                          <span className="text-xs text-primary font-medium">{person.desk.label}</span>
                          <span className="text-xs text-muted-foreground">- {person.desk.floor_name}</span>
                        </div>
                      ) : (
                        <p className="text-xs text-muted-foreground mt-0.5">No desk assigned</p>
                      )}
                    </div>
                  </button>
                ))}
                {mobilePanel === 'search' && searchQuery.length >= 2 && searchResults.length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-6">No results found</p>
                )}

                {/* My bookings */}
                {mobilePanel === 'bookings' && myBookings.filter(b => b.status !== 'cancelled').length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-6">No active bookings</p>
                )}
                {mobilePanel === 'bookings' && myBookings.filter(b => b.status !== 'cancelled').map(booking => (
                  <div key={booking.id} className="p-3 border-b">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium">{booking.desk_label}</p>
                        <p className="text-xs text-muted-foreground">{booking.floor_name} &middot; {booking.date}</p>
                      </div>
                      <Badge variant={booking.status === 'checked_in' ? 'default' : 'secondary'} className="text-[10px]">
                        {booking.status}
                      </Badge>
                    </div>
                  </div>
                ))}

                {/* Who's in */}
                {mobilePanel === 'whosIn' && !whosInData && (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary" />
                  </div>
                )}
                {mobilePanel === 'whosIn' && whosInData && whosInData.users
                  .filter(u => !whosInFilter || u.display_name.toLowerCase().includes(whosInFilter.toLowerCase()))
                  .map(person => (
                  <button
                    key={person.id}
                    data-testid="mobile-whos-in-item"
                    className="w-full text-left p-3 hover:bg-muted/50 flex items-center gap-3 border-b"
                    onClick={() => {
                      if (person.desk) {
                        if (person.desk.floor_id !== selectedFloorId) setSelectedFloorId(person.desk.floor_id);
                        setHighlightedDeskId(person.desk.id);
                        setTimeout(() => setHighlightedDeskId(null), 3000);
                        togglePanel(null);
                      } else {
                        toast.info(`${person.display_name} has no assigned desk`);
                      }
                    }}
                  >
                    <Avatar className="h-8 w-8 shrink-0">
                      {person.photo_url && <AvatarImage src={person.photo_url} alt={person.display_name} />}
                      <AvatarFallback className="text-xs bg-emerald-500/10 text-emerald-700">
                      {person.display_name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                    </AvatarFallback></Avatar>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium truncate">{person.display_name}</p>
                      {person.desk && (
                        <p className="text-xs text-primary">{person.desk.label} - {person.desk.floor_name}</p>
                      )}
                    </div>
                  </button>
                ))}
              </ScrollArea>
            </div>
          </SheetContent>
        </Sheet>
      )}

      {/* Main map area */}
      <div className="flex-1 relative" data-testid="floor-map-canvas">
        {/* Map controls overlay — responsive */}
        <div className="absolute left-2 top-2 md:left-3 md:top-3 z-20 w-[min(340px,calc(100vw-80px))] md:w-[min(380px,calc(100vw-120px))]" data-testid="map-controls">
          <Card className="shadow-lg">
            <CardContent className="p-2 md:p-3 space-y-2">
              {/* Floor select */}
              <Select value={selectedFloorId} onValueChange={setSelectedFloorId} data-testid="floor-select">
                <SelectTrigger className="w-full h-8 md:h-9 text-sm" data-testid="floor-select">
                  <SelectValue placeholder="Select floor" />
                </SelectTrigger>
                <SelectContent>
                  {floors.map(f => (
                    <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
                  ))}
                  {floors.length === 0 && (
                    <div className="px-3 py-2 text-sm text-muted-foreground">No floors configured</div>
                  )}
                </SelectContent>
              </Select>

              {/* Date picker */}
              <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-between h-8 md:h-9 text-xs md:text-sm" data-testid="date-picker">
                    <span className="flex items-center gap-2">
                      <Calendar className="h-3.5 w-3.5 md:h-4 md:w-4" />
                      {format(selectedDate, 'EEE, MMM d yyyy')}
                    </span>
                    <ChevronDown className="h-3 w-3 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <CalendarUI
                    mode="single"
                    selected={selectedDate}
                    onSelect={(d) => { if (d) { setSelectedDate(d); setCalendarOpen(false); } }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>

              {/* Legend — collapsed on mobile, full on desktop */}
              <div className="hidden md:flex flex-wrap gap-x-3 gap-y-1 pt-1" data-testid="map-legend">
                {Object.entries(stateColors).map(([key, val]) => (
                  <div key={key} className="flex items-center gap-1.5">
                    <span className={`h-2.5 w-2.5 rounded-full ring-1 ring-border ${val.bg}`} />
                    <span className="text-[10px] text-muted-foreground">{val.label}</span>
                  </div>
                ))}
                <div className="flex items-center gap-1.5">
                  <span className="h-2.5 w-2.5 rounded-sm ring-1 ring-border bg-emerald-100" />
                  <span className="text-[10px] text-muted-foreground">Room Free</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="h-2.5 w-2.5 rounded-sm ring-1 ring-border bg-red-100" />
                  <span className="text-[10px] text-muted-foreground">Room Busy</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Zoom controls */}
        <div className="absolute right-2 bottom-16 md:right-3 md:bottom-3 z-20 flex flex-col gap-1">
          <Button variant="secondary" size="icon" className="h-8 w-8 md:h-9 md:w-9 shadow" data-testid="zoom-in-button"
            onClick={() => transformRef.current?.zoomIn(0.3)}>
            <ZoomIn className="h-4 w-4" />
          </Button>
          <Button variant="secondary" size="icon" className="h-8 w-8 md:h-9 md:w-9 shadow" data-testid="zoom-out-button"
            onClick={() => transformRef.current?.zoomOut(0.3)}>
            <ZoomOut className="h-4 w-4" />
          </Button>
          <Button variant="secondary" size="icon" className="h-8 w-8 md:h-9 md:w-9 shadow" data-testid="reset-view-button"
            onClick={() => transformRef.current?.resetTransform()}>
            <LocateFixed className="h-4 w-4" />
          </Button>
        </div>

        {/* Map content */}
        {floors.length === 0 && !loading ? (
          <div className="flex flex-col items-center justify-center h-full text-muted-foreground px-4 text-center">
            <Map className="h-12 w-12 md:h-16 md:w-16 mb-4 opacity-30" />
            <p className="text-base md:text-lg font-medium">No floors configured</p>
            <p className="text-sm">Ask an admin to upload a floor map to get started.</p>
            {isAdmin && (
              <Button className="mt-4" onClick={() => navigate('/admin')}>
                <Settings className="h-4 w-4 mr-2" /> Go to Admin Panel
              </Button>
            )}
          </div>
        ) : floorData?.map_image_key ? (
          <TransformWrapper
            ref={transformRef}
            minScale={0.2}
            maxScale={4}
            initialScale={1}
            centerOnInit={true}
            centerZoomedOut={false}
            wheel={{ step: 0.08 }}
            doubleClick={{ disabled: true }}
            panning={{ velocityDisabled: false }}
            limitToBounds={false}
            alignmentAnimation={{ disabled: true }}
          >
            <TransformComponent
              wrapperStyle={{ width: '100%', height: '100%' }}
              contentStyle={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <div className="relative inline-block">
                <img
                  src={getUploadUrl(floorData.map_image_key)}
                  alt={floorData.name || 'Floor map'}
                  className="block select-none max-w-none"
                  draggable={false}
                  style={{ maxWidth: '100%', maxHeight: '100vh', width: 'auto', height: 'auto' }}
                />
                {/* Desk markers */}
                {desks.map(desk => (
                  <DeskMarker
                    key={desk.id}
                    desk={desk}
                    state={getDeskState(desk, user?.id, featureSettings)}
                    isHighlighted={highlightedDeskId === desk.id}
                    onClick={handleDeskClick}
                  />
                ))}
                {/* Meeting room markers */}
                {rooms.map(room => (
                  <MeetingRoomMarker key={room.id} room={room} onClick={handleRoomClick} />
                ))}
              </div>
            </TransformComponent>
          </TransformWrapper>
        ) : loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
            <Map className="h-12 w-12 md:h-16 md:w-16 mb-4 opacity-30" />
            <p className="text-base md:text-lg">Select a floor to view</p>
          </div>
        )}
      </div>

      {/* Mobile bottom navigation bar */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-30 bg-card border-t flex items-center justify-around py-1.5 px-1 safe-area-bottom" data-testid="mobile-bottom-nav">
        <button
          data-testid="mobile-nav-map"
          className={`flex flex-col items-center gap-0.5 px-3 py-1 rounded-lg text-xs ${!mobilePanel ? 'text-primary' : 'text-muted-foreground'}`}
          onClick={() => togglePanel(null)}
        >
          <Map className="h-5 w-5" />
          <span>Map</span>
        </button>
        <button
          data-testid="mobile-nav-search"
          className={`flex flex-col items-center gap-0.5 px-3 py-1 rounded-lg text-xs ${mobilePanel === 'search' ? 'text-primary' : 'text-muted-foreground'}`}
          onClick={() => togglePanel('search')}
        >
          <Search className="h-5 w-5" />
          <span>Find</span>
        </button>
        <button
          data-testid="mobile-nav-bookings"
          className={`flex flex-col items-center gap-0.5 px-3 py-1 rounded-lg text-xs ${mobilePanel === 'bookings' ? 'text-primary' : 'text-muted-foreground'}`}
          onClick={() => togglePanel('bookings')}
        >
          <Bookmark className="h-5 w-5" />
          <span>Bookings</span>
        </button>
        <button
          data-testid="mobile-nav-whos-in"
          className={`flex flex-col items-center gap-0.5 px-3 py-1 rounded-lg text-xs ${mobilePanel === 'whosIn' ? 'text-primary' : 'text-muted-foreground'}`}
          onClick={() => togglePanel('whosIn')}
        >
          <Users className="h-5 w-5" />
          <span>Who's In</span>
        </button>
        {isAdmin && (
          <button
            data-testid="mobile-nav-admin"
            className="flex flex-col items-center gap-0.5 px-3 py-1 rounded-lg text-xs text-muted-foreground"
            onClick={() => navigate('/admin')}
          >
            <Settings className="h-5 w-5" />
            <span>Admin</span>
          </button>
        )}
      </div>

      {/* Desk detail sheet */}
      <DeskDetailSheet
        desk={selectedDesk}
        state={selectedDeskState}
        isOpen={sheetOpen}
        onClose={() => setSheetOpen(false)}
        onBook={handleBookDesk}
        onCancel={handleCancelBooking}
        onCheckin={handleCheckin}
        currentUserId={user?.id}
        requireCheckin={featureSettings.require_checkin === 'true'}
      />
      <RoomDetailSheet
        room={selectedRoom}
        isOpen={roomSheetOpen}
        onClose={() => setRoomSheetOpen(false)}
        dateStr={dateStr}
      />
    </div>
  );
}
